// src/services/userService.js
import { auth, db } from "../firebase";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/**
 * ✅ Get the currently logged-in user's profile
 */
export const getMyProfile = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated");

  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: uid, ...snap.data() } : null;
};

/**
 * ✅ Create or update (upsert) a helper profile
 * If called without userId, will default to current user
 */
export const upsertHelperProfile = async (
  data = {},
  userId = null // optional: allow explicit UID or fallback to current user
) => {
  try {
    const uid = userId || auth.currentUser?.uid;
    if (!uid) throw new Error("User ID is required");

    // ✅ Security: Ensure email is verified before creating/updating profile
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.emailVerified) {
      throw new Error("Email must be verified before creating or updating profile");
    }

    const userRef = doc(db, "users", uid);

    // ✅ Normalize skills
    const normalizedSkills = (data.skills || [])
      .map((s) => String(s).trim().toLowerCase())
      .filter(Boolean);

    // ✅ Handle location - use structured object if provided, otherwise create empty
    const locationData = data.location && typeof data.location === 'object'
      ? data.location
      : (data.location || { city: '', country: '', continent: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });

    // ✅ Merge complete helper info including name & email
    const existing = await getDoc(userRef);
    const profileData = {
      uid,
      role: "helper",
      name: data.name || auth.currentUser?.displayName || "Unnamed Helper",
      email: data.email || auth.currentUser?.email || "",
      bio: data.bio || "",
      skills: normalizedSkills,
      availability: data.availability || "online",
      preferredUrgency: data.preferredUrgency || "medium",
      categoryId: data.categoryId || "",
      location: locationData, // Store as structured object
      experienceLevel: data.experienceLevel || "Intermediate",
      social: data.social || {},
      hoursPerWeek: data.hoursPerWeek || "10-20",
      updatedAt: serverTimestamp(),
    };
    
    // Only set createdAt on first creation
    if (!existing.exists()) {
      profileData.createdAt = serverTimestamp();
    }

    await setDoc(userRef, profileData, { merge: true });

    return { success: true };
  } catch (error) {
    console.error("❌ Error upserting helper profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Fetch a user profile by UID
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      return { success: false, error: "User not found" };
    }

    return { success: true, data: snap.data() };
  } catch (error) {
    console.error("❌ Error fetching user profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Update only specific fields of a helper profile
 */
export const updateHelperProfile = async (userId, updates) => {
  try {
    const userRef = doc(db, "users", userId);

    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("❌ Error updating helper profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Update current user's profile (owner-only)
 */
export const updateMyProfile = async (updates) => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated");
  return updateHelperProfile(uid, updates);
};